import React, { useMemo } from 'react'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
import messageUtil from 'utils/messageUtil'

import { convertToTableData } from './utils'
import { useDashboardWidgetSetting } from '../../../../../hooks/useDashboardState'
import { useDashboardWidgetData } from '../../../../../hooks/useWidgetState'
import TableChart from '../../../../visualization/Table'
import { getNumberFormatter, getThemeColorByCountType } from '../../utils'
import { useDisplayTheme } from '../../../../../hooks/useUiState'
import { InsightCount } from '../../../../../schemas/action/InsightCountSchema'
import { useEmojiNameToDisplayMap } from '../../../../../hooks/useEmojiState'
import { TableSortOption } from '../../../../../enums'
import { ColSortingConfigs } from '../../../../visualization/Table/types'
import { Theme } from '../../../../../features/ui/types'
import { WordCloudWidgetsType, TableSelectionType } from '../../types'

interface TableProps {
  widgetId: number
  onRowClick?: (target: TableSelectionType) => void
}

const Table = ({ widgetId, onRowClick }: TableProps) => {
  const widgetSetting = useDashboardWidgetSetting<WordCloudWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)
  const emojiNameToDisplayMap = useEmojiNameToDisplayMap()

  const rowData = useMemo(
    () => convertToTableData(widgetData.data, widgetSetting, emojiNameToDisplayMap),
    [widgetData, widgetSetting, emojiNameToDisplayMap]
  )
  const theme = useDisplayTheme() as Theme

  const getTableSortConfigs = (): Array<ColSortingConfigs<'value' | 'name'>> => {
    if (widgetSetting.expandableTableSortOptions) {
      return widgetSetting.expandableTableSortOptions.reduce((acc, curr, idx) => {
        if (idx === 0) {
          acc.push({
            field: 'name',
            order: curr!
          })
        } else {
          acc.push({
            field: 'value',
            order: curr!
          })
        }
        return acc
      }, [] as Array<ColSortingConfigs<'value' | 'name'>>)
    }
    return [
      {
        field: 'value',
        order: TableSortOption.DESCEND
      }
    ]
  }

  return (
    <TableChart
      onRowClick={onRowClick}
      rowData={rowData}
      configs={{
        colSortingConfigs: getTableSortConfigs(),
        keyField: 'key'
      }}
      colDefs={[
        {
          field: 'name',
          headerText: `${messageUtil.viewOption(widgetSetting.viewOption!)}`,
          sortable: true,
          cellProps: {
            textAlign: 'left',
            width: '50%',
            colorSetting: (cellValue, context) => context.row.colorKey || ''
          },
          comparator: (rowA, rowB) => (rowA.key! > rowB.key! ? 1 : -1),
          customCellRender: (cellValue) => <span>{cellValue}</span>,
          defaultOrder: TableSortOption.ASCEND
        },
        {
          field: 'value',
          headerText: `${messageUtil.countType(widgetSetting.countType!)}`,
          sortable: true,
          cellProps: {
            textAlign: 'left',
            width: '50%'
          },
          comparator: (rowA, rowB) => (rowA.value > rowB.value ? 1 : -1),
          children: [
            {
              customCellRender: (_, { row }) => <>{getNumberFormatter().format(row.value!)}</>,
              cellProps: {
                colSidePaddings: { right: '8px' }
              }
            },
            {
              customCellRender: (_, { row }) => (
                <>
                  <SimpleBar
                    progress={row.percentage * 100}
                    colorSetting={getThemeColorByCountType(widgetSetting.countType!)}
                    mode={theme}
                  />
                </>
              ),
              cellProps: {
                width: '100%',
                colSidePaddings: { left: '0px' }
              }
            }
          ],
          defaultOrder: TableSortOption.DESCEND
        }
      ]}
      isDisplayFoot={false}
    />
  )
}

export default Table
